import { Box, FormControl, Input, InputLabel, MenuItem, Select, Typography } from "@material-ui/core";
import { services } from "api/serviceConfig";
import { PageRequest } from "api/sharedModels/PageRequest";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import { LynxButton } from "components/LynxButton/LynxButton";
import { LynxCheckBox } from "components/LynxCheckBox/LynxCheckBox";
import { LynxInput } from "components/LynxInput/LynxInput";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useStore } from "store/StoreConfigs";
import { FormProps } from "./FormProps";
import { formStyles } from "./FormStyles";

export default observer(function AddUserForm(props: FormProps) {
    const classes = formStyles();
    const { identityStore, userStore } = useStore();

    const [fields, setFields] = useState({
        email: "",
        firstName: "",
        lastName: "",
        password: "",
        selectedCustomerId: -1,
        groupId: -1,
    });

    const [withPassword, setWithPassword] = useState(false);

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | { name?: string | undefined; value: unknown }>
    ) => {
        setFields({ ...fields, [e.target.name!]: e.target.value });
    };

    const handleCheckBoxChange = (e: any, checked: boolean) => {
        setWithPassword(checked);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (loading) {
            return;
        }

        setLoading(true);
        setErrors([]);

        try {
            const response = await services.UserManagement.addUser({
                ...fields,
                groupId: fields.groupId === -1 ? null : fields.groupId,
                selectedCustomerId: fields.selectedCustomerId === -1 ? null : fields.selectedCustomerId,
                password: withPassword ? fields.password : "",
                customerId: identityStore.currentCustomerId,
            });

            if (response.status !== 200) {
                if (response.status === 400 && response.data.title === "One or more validation errors occurred.") {
                    setErrors(Object.entries(response.data.errors).flatMap((x) => x[1]) as string[]);
                } else if (response.status === 400 && response.data.errorMessage) {
                    setErrors([response.data.errorMessage]);
                } else {
                    console.log(response);
                    setErrors(["Unknown error occured. Check console output"]);
                }
            } else {
                props.successCallback(fields.email);
            }
        } catch (error) {
            console.log(error);
            setErrors(["Unknown error occured. Check console output."]);
        } finally {
            setLoading(false);
        }
    };

    const customerDisplayName = (id: any) => {
        return id === -1 ? "-- global user --" : userStore.customers.find((x) => x.id === id)?.name;
    };

    const groupDisplayName = (id: any) => {
        return id === -1 ? "None" : userStore.groups.find((x) => x.id === id)?.name;
    };

    useEffect(() => {
        const request: PageRequest = {
            pageNumber: 1,
            pageSize: 100,
            customerId: identityStore.currentCustomerId,
        };

        userStore.loadCustomers(request);
    }, []);

    useEffect(() => {
        userStore.loadGroups(fields.selectedCustomerId);
    }, [fields.selectedCustomerId]);

    if (userStore.areCustomersLoading || userStore.groupsLoading) {
        return <LoadingIndicator className={classes.loadingIndicator} />;
    }

    return (
        <Box padding={2}>
            <Typography variant="h3" gutterBottom>
                Add User
            </Typography>
            <form className={classes.form} onSubmit={handleSubmit}>
                <LynxInput
                    size="large"
                    name="email"
                    value={fields.email}
                    label="Email"
                    onChange={handleInputChange}
                    placeholder="jon.snow@gmail.com"
                    formControlClassName={classes.input}
                />
                <LynxInput
                    size="large"
                    name="firstName"
                    value={fields.firstName}
                    label="First Name"
                    onChange={handleInputChange}
                    placeholder="Jon"
                    formControlClassName={classes.input}
                />
                <LynxInput
                    size="large"
                    name="lastName"
                    value={fields.lastName}
                    label="Last Name"
                    onChange={handleInputChange}
                    placeholder="Snow"
                    formControlClassName={classes.input}
                />
                <FormControl className={classes.input}>
                    <InputLabel>Customer</InputLabel>
                    <Select
                        name="selectedCustomerId"
                        value={fields.selectedCustomerId}
                        onChange={handleInputChange}
                        input={<Input id="select-customer-dropdown" />}
                        renderValue={customerDisplayName}
                    >
                        {userStore.customers.map((x) => (
                            <MenuItem key={x.id} value={x.id}>
                                {x.name}
                            </MenuItem>
                        ))}
                        <MenuItem value={-1}>-- global user --</MenuItem>
                    </Select>
                </FormControl>
                <FormControl className={classes.input}>
                    <InputLabel>Group</InputLabel>
                    <Select
                        name="groupId"
                        value={fields.groupId}
                        onChange={handleInputChange}
                        input={<Input id="select-group-dropdown" />}
                        renderValue={groupDisplayName}
                    >
                        <MenuItem value={-1}>None</MenuItem>
                        {userStore.groups.map((x) => (
                            <MenuItem key={x.id} value={x.id}>
                                {x.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <LynxCheckBox
                    checked={withPassword}
                    onChange={handleCheckBoxChange}
                    labelClassName={classes.input}
                    label="With password"
                    size="medium"
                />
                {withPassword && (
                    <LynxInput
                        size="large"
                        name="password"
                        value={fields.password}
                        label="Password"
                        onChange={handleInputChange}
                        formControlClassName={classes.input}
                    />
                )}
                <LynxButton
                    className={classes.submitButton}
                    size="large"
                    responsive={false}
                    type="submit"
                    loading={loading}
                    disabled={loading}
                >
                    Submit
                </LynxButton>
                {errors.length > 0 &&
                    errors.map((x) => (
                        <Typography key={x} variant="body1" className={classes.error}>
                            {x}
                        </Typography>
                    ))}
            </form>
        </Box>
    );
});
